import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { useEffect, useState } from 'react';
import { BulkImportType } from '../../../../../types';
import { BulkOpsFormValues } from './create-bulk-op-dialog';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import { FormSectionHeader } from '../../../../shared/form-section-header';

import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ViewColumn from '@mui/icons-material/ViewColumn';

interface StepSelectColumnsProps {
  control: Control<BulkOpsFormValues>;
  setValue: UseFormSetValue<BulkOpsFormValues>;
  watch: UseFormWatch<BulkOpsFormValues>;
  selectedBulkImportType: BulkImportType | null;
  errors: FieldErrors<BulkOpsFormValues>;
  apiAccessGranted: boolean;
  cloudIdentityBulkOpTypes: string[];
  isNewUi?: boolean;
}

function CustomToolbar() {
  return (
    <SharedToolbar noColumn noFilter>
      <Stack flexDirection="row" gap={1}></Stack>
    </SharedToolbar>
  );
}

export default function StepSelectColumns({
  watch,
  control,
  setValue,
  selectedBulkImportType,
  errors,
  apiAccessGranted,
  cloudIdentityBulkOpTypes,
  isNewUi = false,
}: StepSelectColumnsProps) {
  const selectedColumns = watch('columns') || [];

  const isCloudIdentityBulkOpTypesSelected =
    selectedBulkImportType?.name !== '' &&
    selectedBulkImportType?.name !== undefined &&
    cloudIdentityBulkOpTypes.includes(selectedBulkImportType?.name);

  const possibleColumns = selectedBulkImportType?.columns || [];

  const [selectedRows, setSelectedRows] = useState(
    possibleColumns.filter((pc) =>
      selectedColumns.some((sc) => sc.name === pc.name)
    )
  );

  // handle form state to filter out cloud identity columns
  useEffect(() => {
    if (
      apiAccessGranted === false &&
      selectedColumns.some((column) => column.columnName === 'Created Date')
    ) {
      const filteredColumns = selectedColumns.filter(
        (column) => column.columnName !== 'Created Date'
      );
      setValue('columns', filteredColumns);
      setSelectedRows(filteredColumns);
    }
  }, [apiAccessGranted, selectedColumns]);

  const isColumnDisabled = (row: {
    columnName: string;
    valueName: string;
    name: string;
    isCanned: boolean;
    useInReport: boolean;
    requiredInReport: boolean;
    requiredInBulkOps: boolean;
    showInColumnPicker: boolean;
    type?: string;
  }) => {
    // Check if this is a "Created Date" column that should be conditionally disabled
    if (row.columnName === 'Created Date') {
      const shouldDisable =
        !apiAccessGranted && isCloudIdentityBulkOpTypesSelected;
      return shouldDisable;
    }

    return row.requiredInBulkOps;
  };

  const unselectableRows = possibleColumns.filter((row) =>
    isColumnDisabled(row)
  );
  const selectableRows = possibleColumns.filter(
    (row) => !isColumnDisabled(row)
  );
  const sortedRows = [...unselectableRows, ...selectableRows];

  const columns: GridColDef[] = [
    {
      field: 'columnName',
      headerName: 'Available Columns',
      editable: false,
      sortable: true,
      filterable: false,
      flex: 1,
    },
  ];

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}
      gap={1}
      alignItems={'left'}
    >
      <Stack alignItems="left" sx={{ minHeight: 0 }}>
        {isNewUi ? (
          <FormSectionHeader
            descriptionVariant="body2"
            description="Choose the columns to include in your exported Google Sheet. All columns are selected by default. Required columns can’t be removed."
          >
            Select Columns
          </FormSectionHeader>
        ) : (
          <Typography>
            Optionally provide which columns you would like to have in your
            generated report. All columns are selected by default. Any columns
            that you can not unselect are required and can not be unselected.
          </Typography>
        )}
      </Stack>
      <Stack alignItems="left" sx={{ flex: 1, minHeight: 0 }}>
        <Controller
          name={'columns'}
          control={control}
          render={() => (
            <Box sx={{ flex: 1, minHeight: 0, p: 0 }}>
              <SharedDataGridClient
                slots={{
                  toolbar: CustomToolbar,
                  ...createGridOverlays('Columns', <ViewColumn />),
                }}
                rows={sortedRows}
                columns={columns}
                getRowId={(row) => row?.name}
                rowHeight={38}
                checkboxSelection
                hideFooterRowCount
                keepNonExistentRowsSelected
                disableRowSelectionOnClick
                onRowSelectionModelChange={(newModel) => {
                  const selectedIDs = new Set(newModel.ids);
                  const selectedRowData = possibleColumns.filter((row) =>
                    selectedIDs.has(row.name)
                  );
                  setSelectedRows(selectedRowData);
                  setValue('columns', selectedRowData);
                }}
                selectedRows={selectedRows.map((row) => row.name)}
                isRowSelectable={(params: GridRowParams) =>
                  !isColumnDisabled(params.row)
                }
              />
            </Box>
          )}
        />
      </Stack>
      {errors.columns ? (
        <FormHelperText error>{errors.columns.message}</FormHelperText>
      ) : null}
    </Box>
  );
}
